/**
 * i18next bootstrap — side-effect import. Pulled in once from main.tsx.
 *
 * Polish is the default per FRD §2.1; English is the fallback. The detector
 * checks a manual override in localStorage first, then falls back to the
 * browser language. Signed-in users' `/users/{uid}.locale` wins on bootstrap
 * via `useAuthBootstrap`.
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import pl from "@/i18n/locales/pl.json";
import en from "@/i18n/locales/en.json";
import de from "@/i18n/locales/de.json";

export type SupportedLocale = "pl" | "en" | "de";

export const SUPPORTED_LOCALES: readonly SupportedLocale[] = ["pl", "en", "de"] as const;

/**
 * Locale served at `/` (no prefix). Per FRD §2.1 Polish is default; other
 * locales get a path prefix (e.g. `/en`, `/de`).
 */
export const DEFAULT_LOCALE: SupportedLocale = "pl";

/**
 * `og:locale` tag per locale. Add an entry here when introducing a new locale.
 * Tags follow the BCP-47-ish format Facebook/Open Graph expects.
 */
export const LOCALE_OG_TAG: Record<SupportedLocale, string> = {
  pl: "pl_PL",
  en: "en_US",
  de: "de_DE",
};

/** Marketing landing path for a locale. `pl` → `/`, others → `/<code>`. */
export function localeToLandingPath(locale: SupportedLocale): string {
  return locale === DEFAULT_LOCALE ? "/" : `/${locale}`;
}

/**
 * Extract the implied locale from a marketing-page path, or undefined if the
 * path doesn't belong to the marketing surface. Iterates `SUPPORTED_LOCALES`
 * so adding a locale here is enough — no other call sites need updating.
 */
export function landingPathToLocale(pathname: string): SupportedLocale | undefined {
  if (pathname === "/") return DEFAULT_LOCALE;
  for (const code of SUPPORTED_LOCALES) {
    if (code === DEFAULT_LOCALE) continue;
    if (pathname === `/${code}` || pathname.startsWith(`/${code}/`)) return code;
  }
  return undefined;
}

/**
 * Honor the URL's locale ahead of localStorage and navigator so cold loads on
 * a locale-prefixed marketing path render in that locale instead of flashing
 * Polish (or the user's last-used locale). Returns undefined for non-marketing
 * paths so signed-in users keep their stored preference.
 */
const marketingPathDetector = {
  name: "marketingPath",
  lookup(): string | undefined {
    if (typeof window === "undefined") return undefined;
    return landingPathToLocale(window.location.pathname);
  },
  cacheUserLanguage(): void {},
};

/** Build-time prerender forces locale via `?__prerenderLocale=` (see run-prerender.mjs). */
const prerenderLocaleDetector = {
  name: "prerenderLocale",
  lookup(): string | undefined {
    if (typeof window === "undefined") return undefined;
    const value = new URLSearchParams(window.location.search).get("__prerenderLocale");
    return isSupportedLocale(value) ? value : undefined;
  },
  cacheUserLanguage(): void {},
};

const detector = new LanguageDetector();
detector.addDetector(prerenderLocaleDetector);
detector.addDetector(marketingPathDetector);

void i18n
  .use(detector)
  .use(initReactI18next)
  .init({
    resources: {
      pl: { translation: pl },
      en: { translation: en },
      de: { translation: de },
    },
    fallbackLng: "pl",
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false },
    detection: {
      order: ["prerenderLocale", "marketingPath", "localStorage", "navigator", "htmlTag"],
      lookupLocalStorage: "i18nextLng",
      caches: ["localStorage"],
    },
  });

export function isSupportedLocale(value: unknown): value is SupportedLocale {
  return value === "pl" || value === "en" || value === "de";
}

/** Active UI locale from i18next, with optional fallback (e.g. wedding.settings.language). */
export function resolveAppLanguage(fallback: SupportedLocale = "pl"): SupportedLocale {
  const raw = i18n.resolvedLanguage ?? i18n.language ?? fallback;
  for (const code of SUPPORTED_LOCALES) {
    if (raw.startsWith(code)) return code;
  }
  return fallback;
}

export default i18n;
